import { css } from '@emotion/react';
import {
  AgGridEvent,
  ColDef,
  GetRowIdParams,
  GridPreDestroyedEvent,
  GridReadyEvent,
} from 'ag-grid-community';
import { CustomCellEditorProps } from 'ag-grid-react';
import { set } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { AgTableWithBaseStyle } from '../../../../../components/AgTable/AgTable2';
import { FieldCellEditor } from '../../../../../components/AgTable/cell-editors/FieldCellEditor';
import { FieldNameRenderer } from '../../../../../components/AgTable/cell-renderers/FieldNameRenderer';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import { RendererType } from '../../../../../data-models/field-definition';
import {
  field2ToField3,
  field2ToFormField,
  IField,
  isNumericRenderer,
} from '../../../../../data-models/field2.data-model';
import { getFormatterForGridField } from '../../../../../data-models/field3.data-model';
import {
  IKPIDataValue,
  KPIRequestFrequency,
  periodFromGranularityAndFrequency,
} from '../../../../../data-models/kpi-requests.data-model';
import {
  IKPITableDataModel,
  IKPITemplateSectionDataModel,
} from '../../../../../data-models/kpi-template.data-model';
import { gridAutoHeightCss, gridCenterHeaderCss } from '../../../../../theme/gridStyle';
import {
  isDateOrDateString,
  isInvalidDate,
  plainDateFromDateOrDateString,
} from '../../../../../util/date-utilts';
import { compareBySortOrder } from '../../../../../util/comparators';
import { FMT } from '../../../../../util/formatter-service';
import { ParserService, StandardParserId } from '../../../../../util/parser-service';
import { IFormField } from '../../../../../view-models/form.view-model';
import { companyProfileFinancialsDefaultColDef } from '../../../../CompanyProfiles/commonGridColDefs';
import { generateKPITableColHeaderData, KPITableColHeaderData } from '../../../utils';
import { IFinancialsRowData } from './MSFinancialsGrid';

const defaultColDef = {
  ...companyProfileFinancialsDefaultColDef,
  flex: 1,
};
const kpiGridCss = css`
  ${gridAutoHeightCss};
  ${gridCenterHeaderCss};
`;
export interface IKPIRowData {
  kpiId: number;
  formField: IFormField<unknown>;
  valuesByDate: Record<string, unknown>;
}

type DataByKPIIdDateAndSection = Record<number, Record<string, Record<KpiSection, unknown>>>;

export interface IKPIGridSectionProps {
  companyId: number;
  companyFYEDateString: string;
  kpiRequestFrequency: KPIRequestFrequency;
  kpiRequestGranularity: KPIRequestFrequency | null;
  kpiRequestPeriod: string;
  kpiResponseData: IKPIDataValue[];
  onKPIGridDataUpdated: (section: KpiSection, data: IKPIDataValue[]) => void;
  section: IKPITemplateSectionDataModel;
  disabled?: boolean;
}

export function KPIAgGrid(props: IKPIGridSectionProps) {
  const {
    companyFYEDateString,
    kpiRequestFrequency,
    kpiRequestGranularity,
    kpiRequestPeriod,
    kpiResponseData,
    onKPIGridDataUpdated,
    section,
    disabled = false,
  } = props;
  const gridTemplateSettings = section.meta as IKPITableDataModel;

  const gridHeaderData = generateKPITableColHeaderData({
    fyeDateString: companyFYEDateString,
    periodISODate: kpiRequestPeriod,
    nextPeriod: gridTemplateSettings.section === KpiSection.budget,
    kpiRequestFrequency,
    selectedFrequency: periodFromGranularityAndFrequency(kpiRequestGranularity, kpiRequestFrequency),
  });

  const columnDefs = useMemo(() => {
    const kpisById = (gridTemplateSettings.metrics ?? []).reduce((map, metric) => {
      return map.set(metric.id, metric);
    }, new Map<number, IField<unknown>>());

    return generateKPIColDefs(gridHeaderData, kpisById, disabled);
  }, [gridTemplateSettings.metrics, gridHeaderData, disabled]);

  const kpisToShow = useMemo(() => {
    return gridTemplateSettings.metrics.toSorted(compareBySortOrder);
  }, [gridTemplateSettings.metrics]);

  const rowData = useMemo(() => {
    return toKPIGridRowData(gridHeaderData, gridTemplateSettings.section, kpisToShow, kpiResponseData);
  }, [gridHeaderData, gridTemplateSettings.section, kpisToShow, kpiResponseData]);

  const onDataChange = useCallback(
    (event: AgGridEvent) => {
      const updatedRowData = event.api.getGridOption('rowData');
      const updatedResponseData = fromKPIGridRowData(
        gridTemplateSettings.section,
        updatedRowData as IKPIRowData[]
      );

      onKPIGridDataUpdated(gridTemplateSettings.section, updatedResponseData);
    },
    [gridTemplateSettings.section, onKPIGridDataUpdated]
  );

  function onGridReady(event: GridReadyEvent) {
    event.api.addEventListener('cellValueChanged', onDataChange);
    event.api.addEventListener('pasteEnd', onDataChange);
  }

  function onGridPreDestroyed(event: GridPreDestroyedEvent) {
    event.api.removeEventListener('cellValueChanged', onDataChange);
    event.api.removeEventListener('pasteEnd', onDataChange);
  }

  function getRowId(params: GetRowIdParams<IKPIRowData>) {
    return params.data.kpiId.toString();
  }

  return (
    <div css={kpiGridCss}>
      <AgTableWithBaseStyle
        columnDefs={columnDefs}
        domLayout={'autoHeight'}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
        onGridReady={onGridReady}
        onGridPreDestroyed={onGridPreDestroyed}
        autoSizeStrategy={{ type: 'fitCellContents', skipHeader: false, colIds: ['formField.label'] }}
        rowData={rowData}
        rowGroupPanelShow='never'
        suppressMovableColumns={true}
        suppressRowDrag={true}
        suppressContextMenu={true}
        suppressCsvExport
        suppressExcelExport
        suppressLastEmptyLineOnPaste
      />
    </div>
  );
}

export function KPICellEditor<T extends { formField: IFormField<unknown> }>(props: CustomCellEditorProps<T>) {
  if (!props.data?.formField) {
    return <div>{props.value}</div>;
  }
  return <FieldCellEditor {...props} formField={props.data.formField} />;
}

export function headerDataToColDef(
  headerData: KPITableColHeaderData,
  kpisById: Map<number, IField<unknown>>,
  disabled = false
): ColDef<IKPIRowData | IFinancialsRowData> {
  return {
    cellEditor: KPICellEditor,
    editable: !disabled,
    field: `valuesByDate.${headerData.date}`,
    headerClass: 'centered-header',
    headerName: headerData.title,
    suppressHeaderMenuButton: true,
    valueFormatter: (params) => {
      const field = kpisById.get(params.data?.kpiId ?? -1);
      if (field) {
        const formatter = getFormatterForGridField(field2ToField3(field));
        return formatter(params.value);
      }
      return params.value;
    },
    valueParser: (params) => {
      const rendererType = params.data?.formField?.renderer;
      if (rendererType && isNumericRenderer(rendererType)) {
        return ParserService.get().parse(StandardParserId.Number, params.newValue);
      }
      return params.newValue;
    },
    cellDataType: false,
    minWidth: 100,
  };
}
function generateKPIColDefs(
  headerData: KPITableColHeaderData[],
  kpisById: Map<number, IField<unknown>>,
  disabled: boolean
) {
  return headerData.reduce(
    (acc, header) => {
      acc.push(headerDataToColDef(header, kpisById, disabled));
      return acc;
    },
    [
      {
        cellStyle: { textAlign: 'left' },
        field: 'formField.label',
        headerName: 'KPI',
        minWidth: 200,
        pinned: true,
        suppressHeaderMenuButton: true,
        cellRenderer: FieldNameRenderer,
        cellRendererParams: {
          namePath: 'formField.label',
          descriptionPath: 'formField.description',
        },
      },
    ] as ColDef[]
  );
}

function toKPIGridRowData(
  gridHeaderData: KPITableColHeaderData[],
  section: KpiSection,
  kpisToShow: IField<unknown>[],
  currentData: IKPIDataValue[]
): IKPIRowData[] {
  const dataByKPIIdDateAndSection = currentData.reduce((res, data) => {
    return set(res, [data.kpiId, data.date, data.section], data.value);
  }, {} as DataByKPIIdDateAndSection);

  return kpisToShow.map((kpi) => {
    const formField = field2ToFormField(kpi);
    formField.autoFocus = true;
    formField.fullWidth = true;

    const valuesByDate: IKPIRowData['valuesByDate'] = {};
    gridHeaderData.forEach((header) => {
      valuesByDate[header.date] = dataByKPIIdDateAndSection[kpi.id]?.[header.date]?.[section] ?? null;
    });

    return { kpiId: kpi.id, formField, valuesByDate };
  }, [] as IKPIRowData[]);
}

function fromKPIGridRowData(kpiSection: KpiSection, rowData: IKPIRowData[]): IKPIDataValue[] {
  return rowData.reduce((res, row) => {
    const { formField, kpiId, valuesByDate } = row;
    Object.entries(valuesByDate).forEach(([date, value]) => {
      // FIXME MAGGIE-8284 Remove this once old code is unused.
      // backwards compatibility start
      const formatter = row.formField
        ? FMT.get().getFormatterForFormField(row.formField)
        : (value: unknown) => value;
      const numberFormatValue = formatter(value);
      // backwards compatibility end.

      res.push({
        kpiId,
        date,
        section: kpiSection,
        numberFormatValue,
        value: transformValue(value, formField.renderer),
      } as unknown as IKPIDataValue);
    });
    return res;
  }, [] as IKPIDataValue[]);
}

export function transformValue(value: unknown, renderer: RendererType) {
  if (isDateOrDateString(value, renderer)) {
    return plainDateFromDateOrDateString(value as string | Date);
  }
  // temp solution until we add validation to KPIAgGrid
  if (isInvalidDate(value) || isInvalidNumber(value)) {
    return null;
  }
  if (isEmptyValue(value)) {
    return null;
  }
  return value;
}

function isEmptyValue(value: unknown) {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

export function isInvalidNumber(value: unknown) {
  return typeof value === 'number' && (Number.isNaN(value) || !Number.isFinite(value));
}
